import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $createHeadingNode, HeadingTagType } from '@lexical/rich-text';
import { $setBlocksType } from '@lexical/selection';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuRadioGroup,
    DropdownMenuRadioItem,
    DropdownMenuTrigger,
} from '@talentmesh/ui/components/dropdown-menu';
import { Toggle } from '@talentmesh/ui/components/toggle';
import { $createParagraphNode, $getSelection, $isRangeSelection } from 'lexical';
import { ALargeSmall, ChevronDown, Heading4, Type } from 'lucide-react';
import { useState } from 'react';

type BlockTagType = HeadingTagType | 'p';

export interface BlockFormatMenuButtonProps {
    tag?: BlockTagType;
    disabled?: boolean;
}

const BlockFormatMenuButton = ({ tag, disabled }: BlockFormatMenuButtonProps) => {
    const [editor] = useLexicalComposerContext();
    const [open, setOpen] = useState(false);

    const applyFormat = (blockTag: BlockTagType) => {
        editor.update(() => {
            const selection = $getSelection();
            if ($isRangeSelection(selection)) {
                $setBlocksType(selection, () =>
                    blockTag === 'p' ? $createParagraphNode() : $createHeadingNode(blockTag),
                );
            }
        });
    };

    const onFormatChange = (value: string) => {
        applyFormat(value as BlockTagType);
    };

    return (
        <DropdownMenu open={open} onOpenChange={setOpen}>
            <DropdownMenuTrigger asChild disabled={disabled}>
                <Toggle
                    size="sm"
                    pressed={open}
                    title="Text format type"
                    aria-expanded={open}
                    aria-haspopup="menu"
                    className="text-muted-foreground aria-pressed:bg-primary/10"
                    onMouseDown={(event) => {
                        // Keep the editor selection when interacting with the toolbar.
                        event.preventDefault();
                    }}
                >
                    <ALargeSmall className="size-4" />
                    <ChevronDown className="size-3 opacity-70" />
                </Toggle>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="min-w-40">
                <DropdownMenuRadioGroup value={tag!} onValueChange={onFormatChange}>
                    <DropdownMenuRadioItem value="h4" className="gap-2">
                        <Heading4 className="size-4" />
                        <span className="text-base font-semibold">Heading</span>
                    </DropdownMenuRadioItem>
                    <DropdownMenuRadioItem value="p" className="gap-2">
                        <Type className="size-4" />
                        <span>Text</span>
                    </DropdownMenuRadioItem>
                </DropdownMenuRadioGroup>
            </DropdownMenuContent>
        </DropdownMenu>
    );
};

export default BlockFormatMenuButton;
